/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business 週次レポート画像のアップロード前処理。サーバ側の 1 画像上限（base64 1,000,000 文字 ≒ バイナリ 750KB）に収まるよう、大きい画像を canvas で自動縮小・JPEG 再圧縮してから base64 化する。
 * @graph-connects none
 */

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business 1 画像あたりのバイナリ上限（バイト）。サーバの base64 長上限 1,000,000 文字（= バイナリ 750,000 バイト）と一致させる。
 * @graph-connects none
 */
export const MAX_IMAGE_BINARY_BYTES = 750_000;

/** @graph-connects none */
const MAX_SOURCE_BYTES = 30_000_000;

/** @graph-connects none */
const PASSTHROUGH_MIME_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];

/** @graph-connects none */
const RESIZE_MAX_EDGES = [1920, 1600, 1280, 1024, 800];

/** @graph-connects none */
const JPEG_QUALITIES = [0.85, 0.75, 0.65, 0.55];

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business アップロード用に変換済みの画像ペイロード（MIME タイプ + base64 文字列）。
 * @graph-connects none
 */
export interface ReportImagePayload {
  mimeType: string;
  data: string;
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business 画像前処理でユーザーに提示できる失敗（非画像・上限超過・圧縮不能）。message をそのまま UI に表示する。
 * @graph-connects none
 */
export class ReportImageError extends Error {
  /** @graph-connects none */
  constructor(message: string) {
    super(message);
    this.name = 'ReportImageError';
  }
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business Blob を base64 文字列（data URL プレフィックスなし）に変換する。
 * @graph-connects none
 */
async function blobToBase64(blob: Blob): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : '';
      const commaIndex = result.indexOf(',');
      if (commaIndex < 0) {
        reject(new ReportImageError('画像の読み込みに失敗しました。'));
        return;
      }
      resolve(result.slice(commaIndex + 1));
    };
    reader.onerror = () => {
      reject(new ReportImageError('画像の読み込みに失敗しました。'));
    };
    reader.readAsDataURL(blob);
  });
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business canvas.toBlob を Promise 化する。変換不能（null）は reject。
 * @graph-connects none
 */
async function canvasToBlob(
  canvas: HTMLCanvasElement,
  mimeType: string,
  quality: number,
): Promise<Blob> {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new ReportImageError('画像の変換に失敗しました。'));
        }
      },
      mimeType,
      quality,
    );
  });
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business canvas で描画済みの画像を JPEG 品質を段階的に下げてサイズ上限以下に圧縮する。収まったら ReportImagePayload を返し、全品質で収まらなければ null。
 * @graph-connects none
 */
async function tryCompressCanvas(canvas: HTMLCanvasElement): Promise<ReportImagePayload | null> {
  for (const quality of JPEG_QUALITIES) {
    const blob = await canvasToBlob(canvas, 'image/jpeg', quality);
    if (blob.size <= MAX_IMAGE_BINARY_BYTES) {
      return { mimeType: 'image/jpeg', data: await blobToBase64(blob) };
    }
  }
  return null;
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business 週次レポートに挿入する画像ファイルをアップロード可能なサイズに整える。750KB 以下の対応形式はそのまま base64 化し、それ以上は canvas で長辺 1920px から段階的に縮小＋ JPEG 品質を下げて 750KB 以下に収める。収まらなければ ReportImageError を投げる。
 * @graph-connects none
 */
export async function prepareReportImage(file: File): Promise<ReportImagePayload> {
  if (!file.type.startsWith('image/')) {
    throw new ReportImageError('画像ファイルを選択してください。');
  }
  if (file.size > MAX_SOURCE_BYTES) {
    throw new ReportImageError('画像が大きすぎます（30MB 超）。別の画像を選択してください。');
  }
  if (PASSTHROUGH_MIME_TYPES.includes(file.type) && file.size <= MAX_IMAGE_BINARY_BYTES) {
    return { mimeType: file.type, data: await blobToBase64(file) };
  }
  if (file.type === 'image/gif') {
    throw new ReportImageError(
      `GIF は ${String(Math.round(MAX_IMAGE_BINARY_BYTES / 1000))}KB 以下にしてください（自動圧縮するとアニメーションが失われるため）。`,
    );
  }

  const bitmap = await createImageBitmap(file).catch(() => {
    throw new ReportImageError('画像の読み込みに失敗しました。対応形式か確認してください。');
  });
  try {
    for (const maxEdge of RESIZE_MAX_EDGES) {
      const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(bitmap.width * scale));
      canvas.height = Math.max(1, Math.round(bitmap.height * scale));
      const context = canvas.getContext('2d');
      if (!context) {
        throw new ReportImageError('画像の変換に失敗しました。');
      }
      context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
      const result = await tryCompressCanvas(canvas);
      if (result) return result;
    }
  } finally {
    bitmap.close();
  }
  throw new ReportImageError(
    '画像を圧縮しきれませんでした。画像を分割するか別の画像をお試しください。',
  );
}
